import { useTranslation } from "react-i18next";
import { Activity } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";

import { Timeline } from "../../components/Timeline";
import { ActivityEvent } from "../types";
import {
  activityDetailLines,
  actorLabel,
  activityTypeLabelKey,
  eventTime,
  formatDateTime,
  newestFirst,
  reasonText,
} from "../utils";

interface MarketplaceActivityProps {
  events: ActivityEvent[];
  isLoading: boolean;
  hasError: boolean;
  stockIdByUuid: Record<string, number>;
}

export const MarketplaceActivity = ({
  events,
  isLoading,
  hasError,
  stockIdByUuid,
}: MarketplaceActivityProps) => {
  const { t, i18n } = useTranslation();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="size-4 text-primary" />
          {t("farmersMarket.activity.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Spinner className="size-4" />
            {t("farmersMarket.activity.loading")}
          </div>
        )}
        {hasError && (
          <div className="text-destructive text-sm">{t("farmersMarket.activity.error")}</div>
        )}
        {!isLoading && !hasError && !events.length && (
          <div className="text-muted-foreground text-sm">{t("farmersMarket.activity.empty")}</div>
        )}
        {!isLoading && !hasError && events.length > 0 && (
          <Timeline
            dateColumnClassName="grid-cols-[136px_1fr]"
            items={newestFirst(events, eventTime).map(event => {
              const reason = reasonText(event.reason, i18n.language);
              const detailLines = activityDetailLines(event, t);
              const otherStockId = event.is_this_stock ? null : stockIdByUuid[event.deposit_uuid];
              return {
                key: event.key,
                date: formatDateTime(eventTime(event)),
                title: t(activityTypeLabelKey(event)),
                meta: (
                  <span className="flex flex-wrap items-center gap-2">
                    <span>{actorLabel(event.actor, t)}</span>
                    {otherStockId && (
                      <Badge variant="outline">
                        {t("farmersMarket.activity.onOtherStock", { id: otherStockId })}
                      </Badge>
                    )}
                    {event.is_reconstructed && (
                      <Badge variant="secondary">{t("farmersMarket.activity.reconstructed")}</Badge>
                    )}
                  </span>
                ),
                details:
                  detailLines.length || reason || event.comments ? (
                    <span className="flex flex-col gap-0.5">
                      {detailLines.map(line => (
                        <span key={line}>{line}</span>
                      ))}
                      {reason && <span>{reason}</span>}
                      {event.comments && <span>{event.comments}</span>}
                    </span>
                  ) : undefined,
              };
            })}
          />
        )}
      </CardContent>
    </Card>
  );
};
